import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";
import { mapApiRequestError } from "./apiParseHelpers";
import type {
  Backtest,
  BacktestGetQuery,
  BacktestListQuery,
  BacktestListResponse,
  BacktestPatchBody,
  BacktestScope,
  BacktestSummary,
} from "../types/backtests";

export class BacktestsApiError extends Error {
  status: number;
  detail?: string;
  slug?: string;

  constructor(message: string, status: number, detail?: string, slug?: string) {
    super(message);
    this.name = "BacktestsApiError";
    this.status = status;
    this.detail = detail;
    this.slug = slug;
  }
}

async function parseOrThrow(response: Response, fallbackMessage: string): Promise<any> {
  try {
    return await parseJsonOrThrow(response, fallbackMessage, { allow204: true });
  } catch (err) {
    mapApiRequestError(err, (message, status, parsed) =>
      new BacktestsApiError(message, status, message, parsed.slug)
    );
  }
}

function appendBacktestQueryFlags(params: URLSearchParams, query?: BacktestListQuery | BacktestGetQuery): void {
  if (!query) return;
  if (query.include_snapshot === true) params.append("include_snapshot", "true");
  if (query.include_result === false) params.append("include_result", "false");
}

const listBacktestsInflight = new Map<string, Promise<BacktestListResponse>>();

function listBacktestsRequestKey(query?: BacktestListQuery): string {
  const params = buildListQueryParams(query);
  const pairs = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));
  return pairs.map(([k, v]) => `${k}=${v}`).join("&") || "(default)";
}

function buildListQueryParams(query?: BacktestListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (!query) return params;
  if (query.broker_account_code) params.append("broker_account_code", query.broker_account_code);
  if (query.report_id) params.append("report_id", query.report_id);
  if (query.status) params.append("status", query.status);
  if (typeof query.limit === "number") params.append("limit", String(query.limit));
  if (typeof query.offset === "number") params.append("offset", String(query.offset));
  appendBacktestQueryFlags(params, query);
  return params;
}

function buildDetailQueryParams(query?: BacktestGetQuery): URLSearchParams {
  const params = new URLSearchParams();
  appendBacktestQueryFlags(params, query);
  return params;
}

function normalizeBacktestListResponse(
  raw: unknown,
  query?: BacktestListQuery
): BacktestListResponse {
  const body = raw && typeof raw === "object" ? (raw as Partial<BacktestListResponse>) : {};
  return {
    backtests: Array.isArray(body.backtests) ? body.backtests : [],
    limit: typeof body.limit === "number" ? body.limit : query?.limit ?? 0,
    offset: typeof body.offset === "number" ? body.offset : query?.offset ?? 0,
  };
}

export async function listBacktests(query?: BacktestListQuery): Promise<BacktestListResponse> {
  const key = listBacktestsRequestKey(query);
  const inflight = listBacktestsInflight.get(key);
  if (inflight) return inflight;

  const promise = (async (): Promise<BacktestListResponse> => {
    const params = buildListQueryParams(query);
    const qs = params.toString();
    const endpoint = qs ? `/backtests?${qs}` : "/backtests";
    const response = await authenticatedFetch(endpoint);
    const raw = await parseOrThrow(response, `Failed to fetch simulations: ${response.status}`);
    return normalizeBacktestListResponse(raw, query);
  })();

  listBacktestsInflight.set(key, promise);
  try {
    return await promise;
  } finally {
    listBacktestsInflight.delete(key);
  }
}

export async function getBacktest(backtestId: string, query?: BacktestGetQuery): Promise<Backtest> {
  const params = buildDetailQueryParams(query);
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : "";
  const response = await authenticatedFetch(`/backtests/${encodeURIComponent(backtestId)}${suffix}`);
  return await parseOrThrow(response, `Failed to fetch simulation: ${response.status}`);
}

/** Slim detail row — default BE shape (~30 KB). */
export function getBacktestSlim(backtestId: string): Promise<Backtest> {
  return getBacktest(backtestId);
}

/** Legacy trade-review replay from saved audit bundle (~1.2 MB). */
export function getBacktestWithSnapshot(backtestId: string): Promise<Backtest> {
  return getBacktest(backtestId, { include_snapshot: true });
}

export async function patchBacktest(backtestId: string, body: BacktestPatchBody): Promise<Backtest> {
  const response = await authenticatedFetch(`/backtests/${encodeURIComponent(backtestId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return await parseOrThrow(response, `Failed to update simulation: ${response.status}`);
}

export async function deleteBacktest(backtestId: string): Promise<void> {
  const response = await authenticatedFetch(`/backtests/${encodeURIComponent(backtestId)}`, { method: "DELETE" });
  if (response.ok) return;
  await parseOrThrow(response, `Failed to delete simulation: ${response.status}`);
}

export type BacktestTradeIdsResponse = {
  backtest_id: string;
  kept_trade_ids: string[];
  removed_trade_ids: string[];
  source: "result_meta" | "result" | "recomputed" | string;
};

export async function getBacktestTradeIds(backtestId: string): Promise<BacktestTradeIdsResponse> {
  const response = await authenticatedFetch(`/backtests/${encodeURIComponent(backtestId)}/trade-ids`);
  return await parseOrThrow(response, `Failed to fetch simulation trade ids: ${response.status}`);
}

export type BacktestSymbolsResponse = {
  backtest_id: string;
  symbols: string[];
};

export async function getBacktestSymbols(backtestId: string): Promise<BacktestSymbolsResponse> {
  const response = await authenticatedFetch(`/backtests/${encodeURIComponent(backtestId)}/symbols`);
  return await parseOrThrow(response, `Failed to fetch simulation symbols: ${response.status}`);
}

function normalizeBacktestSummary(raw: unknown): BacktestSummary | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" ? o.id.trim() : "";
  if (!id) return undefined;
  return {
    id,
    title: typeof o.title === "string" ? o.title : o.title == null ? null : undefined,
    policy_tree: o.policy_tree as BacktestSummary["policy_tree"],
    scope: o.scope as BacktestScope | undefined,
    report_id: typeof o.report_id === "string" ? o.report_id : o.report_id == null ? null : undefined,
    strategy_id: typeof o.strategy_id === "string" ? o.strategy_id : o.strategy_id == null ? null : undefined,
    status: typeof o.status === "string" ? o.status : undefined,
  };
}

/** Prefer first occurrence when the same backtest id appears twice in a list. */
export function dedupeBacktestsById<T extends { id?: string | null }>(rows: T[]): T[] {
  const byId = new Map<string, T>();
  for (const row of rows) {
    const id = row?.id?.trim();
    if (!id) continue;
    if (!byId.has(id)) byId.set(id, row);
  }
  return Array.from(byId.values());
}

/** Parse backtest id + summary from simulate save or inline job completion. */
export function parseSimulateBacktestMeta(raw: unknown): {
  backtestId?: string;
  backtestSummary?: BacktestSummary;
} {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const o = raw as Record<string, unknown>;
  const backtestSummary = normalizeBacktestSummary(o.backtest_summary ?? o.backtestSummary);
  const nestedBacktest =
    o.backtest && typeof o.backtest === "object" && !Array.isArray(o.backtest)
      ? (o.backtest as Record<string, unknown>)
      : null;
  const nestedIdRaw = nestedBacktest?.id;
  const nestedId =
    typeof nestedIdRaw === "string" && nestedIdRaw.trim()
      ? nestedIdRaw.trim()
      : typeof nestedIdRaw === "number" && Number.isFinite(nestedIdRaw)
        ? String(nestedIdRaw)
        : undefined;
  const backtestId =
    backtestSummary?.id ||
    (typeof o.backtest_id === "string" && o.backtest_id.trim() ? o.backtest_id.trim() : undefined) ||
    (typeof o.linked_backtest_id === "string" && o.linked_backtest_id.trim()
      ? o.linked_backtest_id.trim()
      : undefined) ||
    nestedId ||
    (typeof o.id === "string" && o.id.trim() && (o.policy_tree != null || o.result != null)
      ? o.id.trim()
      : undefined);
  return { backtestId, backtestSummary };
}
